import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Building2, ChevronRight, MapPin } from "lucide-react";
import { listBusinesses, getAdminToken } from "@/lib/api";
import { toast } from "sonner";
import AdminHeader from "@/components/AdminHeader";
import AdminGate from "@/components/AdminGate";

export default function AdminBusinesses() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  if (!authed) return <AdminGate onAuthed={() => setAuthed(true)} redirect="/admin/businesses" />;
  return <AdminBusinessesInner />;
}

function AdminBusinessesInner() {
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const list = await listBusinesses();
      setRows(list);
    } catch (err) {
      toast.error("Couldn't load businesses.", { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="admin-businesses-page">
      <AdminHeader onRefresh={load} refreshing={busy} active="businesses" />

      <main className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 py-8 page-fade">
        <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          Sprint 1A · Google Listing
        </div>
        <h1
          className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1"
          style={{ color: "var(--ta-primary)" }}
        >
          Businesses
        </h1>
        <p className="mt-2 text-sm max-w-xl" style={{ color: "var(--ta-text-secondary)" }}>
          Every confirmed business and its stored Google Business Listing data. Open a business to inspect the raw payload, normalized fields, and generated observation candidates.
        </p>

        {busy && !rows && (
          <div className="mt-10 flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {rows && rows.length === 0 && (
          <div className="mt-10 rounded-2xl border p-6" style={{ borderColor: "var(--ta-border)", background: "#fff" }}>
            <p className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>
              No confirmed businesses yet. Complete onboarding to add one.
            </p>
          </div>
        )}

        {rows && rows.length > 0 && (
          <ul className="mt-6 flex flex-col gap-3" data-testid="admin-businesses-list">
            {rows.map((b) => (
              <li key={b.id}>
                <Link
                  to={`/admin/businesses/${b.id}`}
                  data-testid={`admin-business-row-${b.id}`}
                  className="flex items-center gap-4 p-5 rounded-2xl border-2 bg-white hover:-translate-y-0.5 transition-transform"
                  style={{ borderColor: "var(--ta-border)" }}
                >
                  <div
                    className="w-11 h-11 rounded-xl inline-flex items-center justify-center shrink-0"
                    style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
                  >
                    <Building2 size={20} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-lg truncate" style={{ color: "var(--ta-primary)" }}>
                      {b.businessName}
                    </div>
                    <div className="text-sm mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5" style={{ color: "var(--ta-text-secondary)" }}>
                      {(b.city || b.state) && (
                        <span className="inline-flex items-center gap-1"><MapPin size={13} /> {[b.city, b.state].filter(Boolean).join(", ")}</span>
                      )}
                      <SourceTag source={b.discoverySource} />
                    </div>
                  </div>
                  <ChevronRight size={18} style={{ color: "var(--ta-text-secondary)" }} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

const SourceTag = ({ source }) => {
  if (!source) return null;
  const isGoogle = source === "google_places";
  return (
    <span
      className="text-[11px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
      style={{
        background: isGoogle ? "var(--ta-accent)" : "var(--ta-surface-alt)",
        color: "var(--ta-primary)",
      }}
    >
      {isGoogle ? "Google" : source}
    </span>
  );
};
